import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Card } from '../ui/card'
import { Button } from '../ui/button'
import { Check, Ban, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export interface DesignConcept {
  name: string
  description: string
  tags: string[]
}

interface BaseProps {
  concepts: DesignConcept[]
  currentConcept: string | null
}

interface SettingsMode extends BaseProps {
  mode?: 'settings'
  projectId: number
  onApplied?: () => void
  onSelect?: never
}

interface SelectMode extends BaseProps {
  mode: 'select'
  onSelect: (conceptName: string | null) => void
  projectId?: never
  onApplied?: never
}

type DesignConceptPickerProps = SettingsMode | SelectMode

export function formatConceptName(name: string): string {
  return name
    .replace(/^design-/, '')
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// Google Fonts URL for non-Inter fonts used in concept titles
const DESIGN_FONTS_URL = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700;800&family=Nunito+Sans:wght@400;500;600;700&display=swap'

// Visual styles from each design spec — card becomes a mini preview
interface ConceptStyle {
  bg: string
  text: string
  muted: string
  accent: string
  accentText: string
  border: string
  titleFont: string
  titleGradient?: string
  titleWeight: number
  titleLetterSpacing: string
}

// Visual identity colors — accent is the most distinctive brand color for each concept
const CONCEPT_STYLES: Record<string, ConceptStyle> = {
  'design-linear-minimal':    { bg: '#08090a', text: '#f7f8f8', muted: '#868a8f', accent: '#5e6ad2', accentText: '#ffffff', border: '#1f2328', titleFont: '"Inter", sans-serif', titleWeight: 500, titleLetterSpacing: '-0.5px' },
  'design-stripe-polished':   { bg: '#ffffff', text: '#0a2540', muted: '#425466', accent: '#533afd', accentText: '#ffffff', border: '#e6ebf1', titleFont: '"Inter", sans-serif', titleWeight: 300, titleLetterSpacing: 'normal' },
  'design-notion-editorial':  { bg: '#ffffff', text: '#37352f', muted: '#9b9a97', accent: '#37352f', accentText: '#ffffff', border: '#e9e9e7', titleFont: '"Inter", sans-serif', titleWeight: 600, titleLetterSpacing: 'normal' },
  'design-vercel-dark':       { bg: '#ffffff', text: '#171717', muted: '#666666', accent: '#171717', accentText: '#ffffff', border: '#eaeaea', titleFont: '"Inter", sans-serif', titleWeight: 600, titleLetterSpacing: '-0.8px' },
  'design-cal-modern':        { bg: '#ffffff', text: '#242424', muted: '#898989', accent: '#242424', accentText: '#ffffff', border: '#e5e5e5', titleFont: '"DM Sans", sans-serif', titleWeight: 700, titleLetterSpacing: 'normal' },
  'design-posthog-dashboard': { bg: '#fdfdf8', text: '#4d4f46', muted: '#7d7f74', accent: '#f54e00', accentText: '#ffffff', border: '#e5e7e0', titleFont: '"IBM Plex Sans", sans-serif', titleWeight: 800, titleLetterSpacing: 'normal' },
  'design-raycast-precise':   { bg: '#07080a', text: '#e5e5e5', muted: '#808080', accent: '#ff6363', accentText: '#ffffff', border: '#1f2629', titleFont: '"Inter", sans-serif', titleWeight: 500, titleLetterSpacing: '0.2px' },
  'design-airbnb-warm':       { bg: '#ffffff', text: '#222222', muted: '#717171', accent: '#ff385c', accentText: '#ffffff', border: '#dddddd', titleFont: '"Nunito Sans", sans-serif', titleWeight: 700, titleLetterSpacing: 'normal' },
  'design-figma-vibrant':     { bg: '#ffffff', text: '#000000', muted: '#666666', accent: '#000000', accentText: '#ffffff', border: '#e6e6e6', titleFont: '"DM Sans", sans-serif', titleWeight: 600, titleLetterSpacing: '-0.3px', titleGradient: 'linear-gradient(135deg, #00c853, #ffd600, #aa00ff, #ff4081)' },
  'design-ibm-enterprise':    { bg: '#ffffff', text: '#161616', muted: '#525252', accent: '#0f62fe', accentText: '#ffffff', border: '#e0e0e0', titleFont: '"IBM Plex Sans", sans-serif', titleWeight: 600, titleLetterSpacing: 'normal' },
  'design-spotify-bold':      { bg: '#121212', text: '#ffffff', muted: '#b3b3b3', accent: '#1ed760', accentText: '#000000', border: '#282828', titleFont: '"DM Sans", sans-serif', titleWeight: 700, titleLetterSpacing: 'normal' },
  'design-wise-clean':        { bg: '#ffffff', text: '#163300', muted: '#6b6b5e', accent: '#9fe870', accentText: '#163300', border: '#e0e0d8', titleFont: '"DM Sans", sans-serif', titleWeight: 900, titleLetterSpacing: 'normal' },
  'design-coinbase-crypto':   { bg: '#ffffff', text: '#0a0b0d', muted: '#6b7280', accent: '#0052ff', accentText: '#ffffff', border: '#e5e7eb', titleFont: '"DM Sans", sans-serif', titleWeight: 600, titleLetterSpacing: '-0.3px' },
  'design-superhuman-focus':  { bg: '#1b1938', text: '#f0ede6', muted: '#a89ec4', accent: '#cbb7fb', accentText: '#1b1938', border: '#2d2a4a', titleFont: '"Inter", sans-serif', titleWeight: 500, titleLetterSpacing: 'normal' },
  'design-framer-creative':   { bg: '#000000', text: '#ffffff', muted: '#a6a6a6', accent: '#0099ff', accentText: '#ffffff', border: 'rgba(0,153,255,0.2)', titleFont: '"DM Sans", sans-serif', titleWeight: 500, titleLetterSpacing: '-1px' },
  'design-apple-minimal':     { bg: '#f5f5f7', text: '#1d1d1f', muted: '#86868b', accent: '#0071e3', accentText: '#ffffff', border: '#d2d2d7', titleFont: '"Inter", sans-serif', titleWeight: 600, titleLetterSpacing: '-0.5px' },
}

const DEFAULT_STYLE: ConceptStyle = { bg: '#f5f5f5', text: '#333', muted: '#888', accent: '#666', accentText: '#fff', border: '#ddd', titleFont: 'sans-serif', titleWeight: 500, titleLetterSpacing: 'normal' }

export function DesignConceptPicker(props: DesignConceptPickerProps) {
  const { concepts, currentConcept } = props
  const { t } = useTranslation('projects')
  const [selected, setSelected] = useState<string | null>(currentConcept)
  const [isApplying, setIsApplying] = useState(false)

  // Load design concept fonts on mount
  useEffect(() => {
    if (!document.querySelector(`link[href="${DESIGN_FONTS_URL}"]`)) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = DESIGN_FONTS_URL
      document.head.appendChild(link)
    }
  }, [])

  const hasChanged = selected !== currentConcept
  const selectedConcept = selected !== null ? concepts.find(c => c.name === selected) : null

  const handleApply = () => {
    if (!hasChanged) return

    if (props.mode === 'select') {
      props.onSelect(selected)
      return
    }

    // Settings mode — PATCH to server
    setIsApplying(true)
    router.patch(`/projects/${props.projectId}/update_design_concept`, {
      design_concept: selected || ''
    }, {
      preserveScroll: true,
      onSuccess: () => {
        const name = selectedConcept ? formatConceptName(selectedConcept.name) : t('settings.design.none')
        toast.success(t('settings.design.toastSet', { name }))
        props.onApplied?.()
      },
      onFinish: () => setIsApplying(false),
    })
  }

  const buttonLabel = props.mode === 'select' ? t('settings.design.select') : t('settings.design.apply')

  return (
    <div className="flex flex-col min-h-0">
      <div className="overflow-y-auto flex-1 min-h-0 p-1">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* None option */}
        <Card
          className={`cursor-pointer group relative overflow-hidden transition-all py-0 ${
            selected === null
              ? 'ring-2 ring-primary'
              : 'hover:border-muted-foreground/30'
          }`}
          onClick={() => setSelected(null)}
        >
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Ban className="h-4 w-4 text-muted-foreground/40" />
                <h3 className="font-medium text-sm">{t('settings.design.none')}</h3>
              </div>
              {selected === null && (
                <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Check className="h-3 w-3 text-primary-foreground" />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('settings.design.noneDescription')}
            </p>
          </div>
        </Card>

        {/* Auto option */}
        <Card
          className={`cursor-pointer group relative overflow-hidden transition-all py-0 ${
            selected === 'auto'
              ? 'ring-2 ring-primary'
              : 'hover:border-muted-foreground/30'
          }`}
          onClick={() => setSelected('auto')}
        >
          <div className="h-6 rounded-t-lg bg-gradient-to-r from-violet-500 via-pink-500 to-orange-500" />
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-500" />
                <h3 className="font-medium text-sm">{t('settings.design.auto')}</h3>
              </div>
              {selected === 'auto' && (
                <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Check className="h-3 w-3 text-primary-foreground" />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('settings.design.autoDescription')}
            </p>
          </div>
        </Card>

        {/* Concept cards — styled as mini previews */}
        {concepts.map((concept) => {
          const isSelected = concept.name === selected
          const s = CONCEPT_STYLES[concept.name] || DEFAULT_STYLE

          return (
            <Card
              key={concept.name}
              className={`cursor-pointer group relative overflow-hidden transition-all py-0 gap-0 ${
                isSelected ? 'ring-2 ring-primary' : ''
              }`}
              style={{ borderColor: s.border }}
              onClick={() => setSelected(concept.name)}
            >
              {/* Header with accent background */}
              <div
                className="px-4 py-3 flex items-center justify-between"
                style={{ backgroundColor: s.accent }}
              >
                <h3 className="text-xl" style={{
                  color: s.titleGradient ? 'transparent' : s.accentText,
                  background: s.titleGradient || 'none',
                  backgroundClip: s.titleGradient ? 'text' : undefined,
                  WebkitBackgroundClip: s.titleGradient ? 'text' : undefined,
                  fontFamily: s.titleFont,
                  fontWeight: s.titleWeight,
                  letterSpacing: s.titleLetterSpacing,
                }}>
                  {formatConceptName(concept.name)}
                </h3>
                {isSelected && (
                  <div className="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3" style={{ color: s.accentText }} />
                  </div>
                )}
              </div>
              {/* Body with concept background */}
              <div className="p-4" style={{ backgroundColor: s.bg }}>
                <p className="text-xs line-clamp-3" style={{ color: s.muted }}>
                  {concept.description}
                </p>
                <div className="flex flex-wrap gap-1 mt-2.5">
                  {concept.tags
                    .filter(tag => tag !== 'design-concept')
                    .slice(0, 3)
                    .map(tag => (
                      <span
                        key={tag}
                        className="text-[10px] px-1.5 py-0.5 rounded-sm font-medium"
                        style={{ backgroundColor: s.accent + '20', color: s.text }}
                      >
                        {tag}
                      </span>
                    ))}
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      </div>
      {/* Apply/Select button — fixed at bottom */}
      <div className="flex justify-end pt-4 border-t mt-4 flex-shrink-0">
        <Button
          onClick={handleApply}
          disabled={!hasChanged || isApplying}
        >
          {isApplying ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
          ) : null}
          {buttonLabel}
        </Button>
      </div>
    </div>
  )
}
